<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Navblock } from '#library/components.schema.build.ts'
	import Render from '#components/render.svelte'
	import { page } from '$app/state'
	import { same_page } from '#library/samepage.ts'
	import { slide } from 'svelte/transition'
	import Image from '#components/image.svelte'

	interface Props {
		blok: Blok<Navblock>
	}

	let { blok }: Props = $props()
	let open = $state<Array<string>>([])

	function toggle(item: string) {
		open = open.includes(item) ? open.filter(i => i !== item) : [...open, item]
	}
</script>

<section class="navblock {blok.theme || 'theme-crocus-on-light-gimson'}" {...attrs(blok)}>
	<div class="contain">
		<div>
			<svelte:element this={blok.title?.tag || 'div'} class="title">
				{blok.title?.text}
			</svelte:element>
			{#if blok.body?.content?.[0]?.content}
				<div class="body">
					<Render content={blok.body} />
				</div>
			{/if}
			{#if blok.image?.filename}
				<div class="image">
					<Image src={blok.image.filename} alt={blok.title?.text} focus={blok.image.focus} aspect="16/9" />
				</div>
			{/if}
		</div>
		<div class="links">
			{#if blok.root?.cached_url}
				{#each blok.links as { name, link, children } (link)}
					{#if children.length}
						<button class="link" class:active={same_page(link, page.url)} onclick={() => toggle(link)}>
							{name}
							<svg class="arrow">
								<use xlink:href="/images/vector.svg#chevron-up"></use>
							</svg>
						</button>
						{#if open.includes(link)}
							<div class="children" transition:slide>
								<a class="sublink" class:active={same_page(link, page.url)} href={link}>
									{name}
								</a>
								{#each children as { name, link } (link)}
									<a class="sublink" class:active={same_page(link, page.url)} href={link}>
										{name}
									</a>
								{/each}
							</div>
						{/if}
					{:else}
						<a class="link" class:active={same_page(link, page.url)} href={link}>
							{name}
						</a>
					{/if}
				{/each}
			{/if}
		</div>
	</div>
</section>

<style lang="css">
	.navblock {
		background-color: var(--c-background);
		color: var(--c-foreground);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
		display: grid;

		@media (--from-mobile) {
			grid-template-columns: 7fr 1fr 4fr;
		}
	}

	.title {
		font: 500 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.body {
		color: var(--c-midground);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);

		:global(a) {
			color: var(--c-foreground);
			font-weight: 600;
		}
	}

	.image {
		@media (--until-mobile) {
			margin-block-end: 1rem;
		}
	}

	.links {
		align-self: start;
		display: grid;
		gap: 1rem;
		padding-block-start: 0.5rem;

		@media (--from-mobile) {
			grid-column: 3;
		}
	}

	.link,
	.sublink {
		border-bottom: 1px solid currentColor;
		color: var(--c-midground);
		display: flex;
		font: 400 var(--f-body1);
		justify-content: space-between;
		letter-spacing: var(--ls-body1);
		padding-block-end: 0.5rem;
		text-transform: uppercase;
		text-align: start;

		&.active,
		&:hover {
			color: var(--c-foreground);
		}
	}

	.arrow {
		rotate: 180deg;
		size: 1.75rem;
	}

	.children {
		display: grid;
		gap: 1rem;
		padding-inline-start: 1rem;
	}

	.sublink {
		font: 500 var(--f-body2);
		letter-spacing: var(--ls-body2);
		text-transform: none;
	}
</style>
